import './index.css'
import cn from 'clsx'
import lodashSort from 'lodash/sortBy'
import * as React from 'react'
import Modal from 'react-modal'
import { connect } from 'react-redux'
import scrollIntoView from 'scroll-into-view-if-needed'
import { PHONE } from '@disco/constants/DeviceTypes'
import {
  getContactTags,
  getPlaylistTagsNames,
} from '@disco/state-management/selectors/tags'
import { convertRemToPx } from '@disco/utils/dom'
import { keyEqualTo } from '@disco/utils/keyboard'
import { matchTag, tagScore } from '@disco/utils/tagSearch'
import Icon from 'components/ui/icon'
import { hamburgerModalStyles } from 'components/utils/modalStyles'

const mapStateToProps = (state, { tagType }) => ({
  tags:
    tagType === 'contact' ? getContactTags(state) : getPlaylistTagsNames(state),
  phoneAlignment: state.app.mobileDeviceType === PHONE,
})

type Props = {
  position:
    | {
        left: number
        top: number
        right: number
        bottom: number
      }
    | null
    | undefined
  onClose?: (...args: Array<any>) => any
  onSelect: (value: string) => any
  selectedTags: Array<string>
  partiallySelectedTags?: Array<string>
  tags: Array<string>
  allowCreate?: boolean
  align?: string
  phoneAlignment: boolean
  tagType: 'playlist' | 'contact'
  preventMenuClose?: boolean
  displaySelectedTags?: boolean
}
type State = {
  value: string
  focusIndex: number
  availableTags: string[]
  visibleSelectedTags: string[]
  visiblePartiallySelectedTags: string[]
}

class TagMenu extends React.Component<Props, State> {
  tagInput: HTMLInputElement | null | undefined
  tagContainer: HTMLUListElement | null | undefined
  scrollToFocusedItem = () => {
    if (!this.tagContainer) return
    const focusedItems = this.tagContainer.getElementsByClassName('is-focused')

    if (focusedItems.length > 0) {
      const focusedItem = focusedItems[0]
      scrollIntoView(focusedItem, {
        behavior: 'smooth',
        scrollMode: 'if-needed',
        boundary: this.tagContainer,
      })
    }
  }
  getUpdatedState = (props: Props) => {
    const { tags, selectedTags, partiallySelectedTags } = props
    return {
      value: '',
      focusIndex: 0,
      availableTags: tags.filter(
        t => ![...selectedTags, ...(partiallySelectedTags || [])].includes(t),
      ),
      visibleSelectedTags: selectedTags || [],
      visiblePartiallySelectedTags: partiallySelectedTags || [],
    }
  }
  state = this.getUpdatedState(this.props)

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      this.props.tags !== nextProps.tags ||
      this.props.selectedTags !== nextProps.selectedTags ||
      this.props.partiallySelectedTags !== nextProps.partiallySelectedTags
    ) {
      this.setState(this.getUpdatedState(nextProps))
    }
  }

  componentDidMount() {
    setTimeout(() => {
      if (this.tagInput) this.tagInput.focus()
    }, 50)
  }

  _getStyles = () => {
    if (!this.props.position) return undefined
    const { left, bottom, right } = this.props.position
    const styles = {
      ...hamburgerModalStyles,
      content: {
        ...hamburgerModalStyles.content,
        width: '14rem',
        backgroundColor: 'transparent',
        boxShadow: 'none',
        borderRadius: 0,
      } as React.CSSProperties,
    }
    // on phones tag menu should open to the left from the container
    let horizontalOffset = 0

    if (!this.props.phoneAlignment) {
      horizontalOffset =
        this.props.align === 'right'
          ? convertRemToPx(7)
          : (right - left) / 2 + convertRemToPx(7)
    }

    styles.content.left = this.props.align === 'right' ? right : left
    styles.content.top = bottom
    styles.content.transform = `translate(calc(-100% + ${horizontalOffset}px), .5rem)`
    return styles
  }
  _onChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    this._updateAvailableTags(e.target.value)
  _updateAvailableTags = (value: string) => {
    const { tags, selectedTags, partiallySelectedTags } = this.props
    const availableTags = tags.filter(t => {
      return (
        [...selectedTags, ...(partiallySelectedTags || [])].indexOf(t) === -1 &&
        (value === '' || matchTag(t, value))
      )
    })
    this.setState({
      value: value,
      focusIndex: 0,
      availableTags:
        value === ''
          ? availableTags
          : lodashSort(availableTags, tag => tagScore(tag, value)).reverse(),
      visibleSelectedTags:
        value === ''
          ? selectedTags
          : selectedTags.filter(t => matchTag(t, value)),
      visiblePartiallySelectedTags:
        value === ''
          ? partiallySelectedTags || []
          : (partiallySelectedTags || []).filter(t => matchTag(t, value)),
    })
  }
  _onHover = index =>
    this.setState({
      focusIndex: index,
    })
  _onKeyUp = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const {
      focusIndex,
      availableTags,
      value,
      visibleSelectedTags,
      visiblePartiallySelectedTags,
    } = this.state
    const { displaySelectedTags } = this.props
    const selectKey = keyEqualTo(e, 'Tab') || keyEqualTo(e, 'Enter')
    const newTagCorrection = value !== '' && this._isNewTag(value) ? 1 : 0
    const shownTags = displaySelectedTags
      ? [
          ...availableTags,
          ...visibleSelectedTags,
          ...visiblePartiallySelectedTags,
        ]
      : availableTags

    if (selectKey && shownTags.length > focusIndex) {
      this.props.onSelect(shownTags[focusIndex])
    } else if (selectKey && shownTags.length === focusIndex && value !== '') {
      // new tag
      this.props.onSelect(value.trim())
    } else if (keyEqualTo(e, 'ArrowUp') && shownTags.length > 0) {
      const index =
        focusIndex > 0
          ? focusIndex - 1
          : shownTags.length - 1 + newTagCorrection
      this.setState(
        {
          focusIndex: index,
        },
        () => this.scrollToFocusedItem(),
      )
    } else if (keyEqualTo(e, 'ArrowDown') && shownTags.length > 0) {
      const index =
        focusIndex < shownTags.length - 1 + newTagCorrection
          ? focusIndex + 1
          : 0
      this.setState(
        {
          focusIndex: index,
        },
        () => this.scrollToFocusedItem(),
      )
    }
  }
  _isNewTag = value =>
    !this.props.tags.some(
      tag => tag.toLowerCase() === value.toLowerCase().trim(),
    )
  _onSelect = (e: React.MouseEvent<HTMLLIElement>, value: string) => {
    if (this.props.preventMenuClose) {
      e.preventDefault()
      e.stopPropagation()
    }

    this.props.onSelect(value)
  }
  _renderTagList = (
    tags: string[],
    offset: number = 0,
    type?: 'selected' | 'partially-selected' | 'available',
  ) => {
    const { focusIndex } = this.state
    return tags.map((t, i) => {
      const offsetIndex = i + offset
      const itemClass = cn('TagMenu_Autocomplete_Item', {
        'is-focused': offsetIndex === focusIndex,
        'is-selected': type === 'selected',
        'is-partially-selected': type === 'partially-selected',
      })
      let marker: React.ReactNode = ''
      if (type === 'selected')
        marker = <Icon type="check" width="1.3em" height="1.3em" />
      if (type === 'partially-selected')
        marker = <Icon type="remove" width="1.3em" height="1.3em" />
      return (
        <li
          className={itemClass}
          key={offsetIndex}
          onMouseOver={() => this._onHover(offsetIndex)}
          onClick={(e: React.MouseEvent<HTMLLIElement>) => this._onSelect(e, t)}
        >
          {marker}
          {t}
        </li>
      )
    })
  }

  render() {
    const {
      focusIndex,
      availableTags,
      value,
      visibleSelectedTags,
      visiblePartiallySelectedTags,
    } = this.state
    const createTagClass = cn('TagMenu_Autocomplete_Item', {
      'is-focused': this.props.displaySelectedTags
        ? availableTags.length +
            visibleSelectedTags.length +
            visiblePartiallySelectedTags.length ===
          focusIndex
        : availableTags.length === focusIndex,
    })
    const createTag =
      this._isNewTag(value) && value !== '' && this.props.allowCreate ? (
        <li
          className={createTagClass}
          onClick={(e: React.MouseEvent<HTMLLIElement>) =>
            this._onSelect(e, value.trim())
          }
        >
          {`Create tag "${value.trim()}"`}
        </li>
      ) : null
    const tagMenu = (
      <div className="TagMenu" onKeyUp={this._onKeyUp}>
        <input
          className="TagMenu_Input no-outline"
          value={value}
          onChange={this._onChange}
          placeholder={
            this.props.allowCreate
              ? 'Find tag or type to add new tag'
              : 'Find tag'
          }
          ref={r => {
            this.tagInput = r
          }}
        />
        <ul
          className="TagMenu_Autocomplete"
          ref={r => {
            this.tagContainer = r
          }}
        >
          {this._renderTagList(availableTags)}
          {this.props.displaySelectedTags &&
            this._renderTagList(
              visibleSelectedTags,
              availableTags.length,
              'selected',
            )}
          {this.props.displaySelectedTags &&
            this._renderTagList(
              visiblePartiallySelectedTags,
              availableTags.length + visibleSelectedTags.length,
              'partially-selected',
            )}
          {createTag}
        </ul>
      </div>
    )
    if (!this.props.position || !this.props.onClose) return tagMenu
    return (
      <Modal
        contentLabel="Find tag"
        onRequestClose={this.props.onClose}
        isOpen
        style={this._getStyles()}
      >
        {tagMenu}
      </Modal>
    )
  }
}

export default connect(mapStateToProps, {})(TagMenu) as any
